import React from 'react'

import {
  Box,
  List,
  ListItemButton,
  Typography,
  IconButton,
} from '@mui/material'
import {
  IconBolt,
  IconBuildingSkyscraper,
  IconCalendar,
  IconFileImport,
  IconFiles,
  IconMicrophone,
  IconPlus,
  IconSearch,
  IconServer,
  IconTrophy,
  IconUser,
} from '@tabler/icons-react'

import { routes, Link } from '@redwoodjs/router'
import { useLocation } from '@redwoodjs/router'

import CollapsibleNavSection, {
  SimpleNavItem,
} from 'src/layouts/ModernLayout/CollapsibleNavSection'
import PipelineNavItems, {
  PipelineHeaderAction,
} from 'src/layouts/ModernLayout/PipelineNavItems'
import ProfileController from 'src/layouts/ModernLayout/ProfileController/ProfileController'

interface NavigationContentProps {
  onSearchClick: () => void
  selectedWorkspace: string | null
  userCoreContact: any
}

// Reusable NavItem component (from ModernLayout)
interface NavItemProps {
  icon: React.ReactElement
  label: string
  route: string
}

const navItemButtonSx = {
  height: '40px',
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  borderRadius: '4px',
  my: 0.5,
  mx: 0,
  px: '14px',
  opacity: 0.8,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: (theme) => theme.palette.secondary.light,
    opacity: 1,
  },
  '& .tabler-icon': {
    flexShrink: 0,
    marginRight: '12px',
  },
}

const navItemButtonActiveSx = {
  ...navItemButtonSx,
  backgroundColor: (theme) => theme.palette.secondary.light,
}

const NavItem = React.memo(({ icon, label, route }: NavItemProps) => {
  const location = useLocation()
  const isCurrentRoute = location.pathname === route

  return (
    <ListItemButton
      component={Link}
      to={route}
      sx={{
        ...(isCurrentRoute ? navItemButtonActiveSx : navItemButtonSx),
        position: 'relative',
      }}
      className="nav-item-button"
    >
      {React.cloneElement(icon, {
        size: 20,
        stroke: 2,
      } as any)}
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: isCurrentRoute ? 600 : 500,
          color: (theme) => theme.palette.text.primary,
          fontFamily: 'Poppins',
        }}
      >
        {label}
      </Typography>
    </ListItemButton>
  )
})

NavItem.displayName = 'NavItem'

const NavigationContent: React.FC<NavigationContentProps> = ({
  onSearchClick,
  selectedWorkspace,
  userCoreContact,
}) => {
  return (
    <>
      <Box sx={{ px: 1 }}>
        <ListItemButton
          onClick={onSearchClick}
          sx={{
            height: '40px',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            borderRadius: '4px',
            my: 0,
            mx: 0,
            px: '14px',
            opacity: 0.8,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.secondary.light,
              opacity: 1,
            },
          }}
        >
          <IconSearch
            size={20}
            stroke={2}
            style={{ marginRight: '12px' }}
          />
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: (theme) => theme.palette.text.primary,
              fontFamily: 'Poppins',
            }}
          >
            Search
          </Typography>
        </ListItemButton>
      </Box>

      <List
        sx={{
          p: 1,
          pt: 0,
          height: 'calc(100vh - 148px)',
          overflowY: 'auto',
        }}
      >
        {/* Standalone navigation items */}
        <NavItem
          label="Home"
          icon={<IconCalendar />}
          route={routes.home()}
        />

        <NavItem
          label="Actions"
          icon={<IconBolt />}
          route={routes.actions()}
        />

        <NavItem
          label="Recordings"
          icon={<IconMicrophone />}
          route={routes.recordings()}
        />

        {/* Opportunities section */}
        <CollapsibleNavSection
          label="Pipelines"
          icon={<IconTrophy />}
          headerAction={selectedWorkspace && <PipelineHeaderAction />}
        >
          {selectedWorkspace && (
            <PipelineNavItems workspaceId={selectedWorkspace} />
          )}
        </CollapsibleNavSection>

        {/* Records section */}
        <CollapsibleNavSection
          label="Records"
          icon={<IconServer />}
          headerAction={
            selectedWorkspace && (
              <RecordsHeaderAction selectedWorkspace={selectedWorkspace} />
            )
          }
        >
          <SimpleNavItem
            label="People"
            route={routes.people()}
            icon={<IconUser />}
          />
          <SimpleNavItem
            label="Organizations"
            route={routes.organizations()}
            icon={<IconBuildingSkyscraper />}
          />
          <SimpleNavItem
            label="Pages"
            route={routes.pages()}
            icon={<IconFiles />}
          />
          {selectedWorkspace && (
            <SimpleNavItem
              label="Imports"
              route={routes.import({
                workspaceId: selectedWorkspace,
              })}
              icon={<IconFileImport />}
            />
          )}
        </CollapsibleNavSection>
      </List>

      {userCoreContact && (
        <ProfileController userCoreContact={userCoreContact} />
      )}
    </>
  )
}

// Records header action component for import button
const RecordsHeaderAction = ({
  selectedWorkspace,
}: {
  selectedWorkspace: string
}) => {
  return (
    <IconButton
      component={Link}
      to={routes.import({ workspaceId: selectedWorkspace })}
      size="small"
      sx={{
        width: 24,
        height: 24,
        p: 0,
        opacity: 0.7,
        '&:hover': {
          opacity: 1,
        },
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <IconPlus size={16} />
    </IconButton>
  )
}

export default NavigationContent
